import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
    Legend
} from 'recharts';
import './DashboardCharts.css';

import type { AnalyticsTopCompany, AnalyticsFuelType, AnalyticsSuccessRate } from '../../services/api';

interface TopCompaniesProps {
    data?: AnalyticsTopCompany[];
}

interface SuccessRateProps {
    data?: AnalyticsSuccessRate[];
}

interface FuelTypeProps {
    data?: AnalyticsFuelType[];
}

export const TopCompaniesChart: React.FC<TopCompaniesProps> = ({ data = [] }) => (
    <div className="chart-card">
        <h3 className="chart-title">Top Companies by Spending</h3>
        <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={260}>
                <BarChart layout="vertical" data={data} margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                    <XAxis type="number" hide />
                    <YAxis
                        dataKey="name"
                        type="category"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        width={100}
                    />
                    <Tooltip
                        cursor={{ fill: '#f8fafc' }}
                        contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        formatter={(value: number) => `₦${value.toLocaleString()}`}
                    />
                    <Bar dataKey="spending" fill="#ff6b35" radius={[0, 4, 4, 0]} barSize={24} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    </div>
);

export const SuccessRateChart: React.FC<SuccessRateProps> = ({ data = [] }) => (
    <div className="chart-card">
        <h3 className="chart-title">Transaction Success Rate (Last 7 Days)</h3>
        <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data}>
                    <defs>
                        <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 11 }}
                    />
                    <YAxis hide domain={[0, 100]} />
                    <Tooltip
                        contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    />
                    <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#10b981"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorRate)"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    </div>
);

export const FuelTypeDonut: React.FC<FuelTypeProps> = ({ data = [] }) => {
    // Map colors to fuel types consistently
    const getColor = (type?: string) => {
        if (!type) return '#8b5cf6';
        const lower = type.toLowerCase();
        if (lower.includes('petrol')) return '#ff6b35';
        if (lower.includes('diesel')) return '#3b82f6';
        return '#8b5cf6'; // fallback
    };

    const cleanData = (data || []).filter(item => !item.fuelType?.toLowerCase().includes('gas'));

    if (cleanData.length === 0) {
        return (
            <div className="chart-card">
                <div className="chart-header-row">
                    <h3 className="chart-title">Fuel Type Distribution</h3>
                </div>
                <div className="no-data-message-container" style={{ padding: '2rem 1rem', textAlign: 'center', color: '#64748b' }}>
                    <p>No fuel distribution data available.</p>
                </div>
            </div>
        );
    }

    const formattedData = cleanData.map(item => ({
        name: item.fuelType || 'Unknown',
        value: item.percentage || 0,
        color: getColor(item.fuelType)
    }));


    return (
        <div className="chart-card">
            <div className="chart-header-row">
                <h3 className="chart-title">Fuel Type Distribution</h3>
                <div className="chart-badge">Daily</div>
            </div>
            <div className="chart-wrapper donut-container">
                <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                        <Pie
                            data={formattedData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                        >
                            {formattedData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => `${value}%`} />
                        <Legend
                            verticalAlign="bottom"
                            align="center"
                            iconType="circle"
                            wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }}
                        />
                    </PieChart>
                </ResponsiveContainer>
                {data.some(d => d.percentage > 0) && (
                    <div className="donut-center-text">
                        <span className="center-value">Optimal</span>
                        <span className="center-label">Usage</span>
                    </div>
                )}
            </div>
        </div>
    );
};
